import React, { useState, useEffect } from 'react';
import { ajax } from '@hms-dbmi-bgm/shared-portal-components/es/components/util';
import Select from 'react-select';
import {
    OverlayTrigger,
    Popover,
    PopoverHeader,
    PopoverBody,
} from 'react-bootstrap';

// Renders a table cell with a value or a dash if no value is present
const TableCell = ({ value, isCode = false }) => {
    const hasValue =
        value !== undefined &&
        value !== null &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0);

    return (
        <td className={`text-left ${!hasValue ? 'text-secondary' : ''}`}>
            {hasValue ? isCode ? <code>{value}</code> : value : '-'}
        </td>
    );
};

// Renders a table of schema properties
const SchemaPropertiesTable = ({
    selectedProperty = null,
    schemaKey = '',
    data = {},
}) => {
    // sort the keys based on requirement
    const sortedPropertyKeys = Object.keys(data).sort((a, b) => {
        return data[a]?.is_required ? -1 : 1;
    });

    return sortedPropertyKeys.length > 0 ? (
        <table className="table table-bordered table-striped">
            <thead className="thead-smaht">
                <tr>
                    <th className="text-left">Metadata Property</th>
                    <th className="text-left">Description</th>
                    <th className="text-left">Type</th>
                    <th className="text-left">Pattern</th>
                    <th className="text-left">
                        Values
                        <OverlayTrigger
                            trigger={['hover', 'focus']}
                            overlay={
                                <Popover className="description-definitions-popover">
                                    <PopoverHeader>Values</PopoverHeader>
                                    <PopoverBody className="p-3">
                                        <p>
                                            <b>Options:</b> Accepted values for
                                            this property.
                                        </p>
                                        <p>
                                            <b>Examples:</b> Suggested values
                                            for this property. Other values
                                            accepted.
                                        </p>
                                    </PopoverBody>
                                </Popover>
                            }
                            placement="top"
                            flip={true}
                            popperConfig={{
                                modifiers: [
                                    {
                                        name: 'flip',
                                        options: {
                                            fallbackPlacements: [
                                                'right',
                                                'bottom',
                                                'left',
                                            ],
                                        },
                                    },
                                ],
                            }}>
                            <i className="icon icon-info-circle fas ms-1"></i>
                        </OverlayTrigger>
                    </th>
                    <th className="text-left">Also Requires</th>
                    <th className="text-left">LinkTo</th>
                    <th className="text-left">Note</th>
                </tr>
            </thead>
            <tbody>
                {sortedPropertyKeys
                    .filter((property) => {
                        return selectedProperty
                            ? property === selectedProperty
                            : true;
                    })
                    .map((propertyKey, i) => {
                        const item = data[propertyKey];

                        // Get a backup description from backup_property_values if needed
                        let description_from_map;
                        if (!item?.description) {
                            description_from_map = backup_property_values
                                ?.get(schemaKey)
                                ?.find(
                                    ({ title }) => title === propertyKey
                                )?.description;
                        }

                        // Build enum/suggested enum cell content
                        const hasEnum = item?.enum?.length > 0;
                        const hasSuggested = item?.suggested_enum?.length > 0;

                        const valuesContent =
                            hasEnum || hasSuggested ? (
                                <>
                                    {hasEnum && (
                                        <p>
                                            <b>Options:</b>{' '}
                                            {item.enum.join(', ')}
                                        </p>
                                    )}
                                    {hasSuggested && (
                                        <p>
                                            <b>Examples:</b>{' '}
                                            {item.suggested_enum.join(', ')}
                                        </p>
                                    )}
                                </>
                            ) : null;

                        return (
                            <tr key={i}>
                                {/* Title */}
                                <td
                                    className={`text-left ${
                                        item?.is_required
                                            ? 'text-danger fw-bold'
                                            : ''
                                    }`}>
                                    {item?.title ?? propertyKey}
                                </td>
                                {/* Description */}
                                <TableCell
                                    value={
                                        item?.description ??
                                        description_from_map
                                    }
                                />
                                {/* Type */}
                                <TableCell value={item?.type} isCode />
                                {/* Pattern */}
                                <TableCell value={item?.pattern} />
                                {/* Values */}
                                <TableCell value={valuesContent} />
                                {/* Also Requires */}
                                <TableCell
                                    value={
                                        item?.also_requires?.length
                                            ? item.also_requires.join(', ')
                                            : null
                                    }
                                />
                                {/* LinkTo */}
                                <TableCell value={item?.items?.linkTo} />
                                {/* Note */}
                                <TableCell value={item?.submissionComment} />
                            </tr>
                        );
                    })}
            </tbody>
        </table>
    ) : (
        'No Schema properties available.'
    );
};

// Requests the submission schemas JSON and redners as a list of tables
export const SubmissionDataDictionary = () => {
    const [schemaData, setSchemaData] = React.useState(null);
    const [selectedSchema, setSelectedSchema] = React.useState(null);

    useEffect(() => {
        ajax.load(
            '/submission-schemas/?format=json',
            (resp) => {
                // Handle the response data
                setSchemaData(resp);
            },
            'GET',
            (err) => {
                // Handle the error
                console.error('Error fetching schema reference:', err);
            }
        );
    }, []);

    const options = Object.keys(schemaData || {}).flatMap((schemaItemName) => {
        const schemaProperties = schemaData[schemaItemName]?.properties || {};

        return [
            { value: schemaItemName, label: schemaItemName },
            ...Object.keys(schemaProperties)?.map((propertyName) => {
                const value = `${schemaItemName}.${propertyName}`;
                return { value: value, label: value?.toLowerCase() };
            }),
        ];
    });

    // Split selected schema into item type and property
    // E.g. AlignedReads.read_length -> [AlignedReads, read_length]
    // If only item type is selected, property will be null
    const selectedSchemaItem = selectedSchema?.value?.split('.')?.[0] || null;
    const selectedSchemaProperty =
        selectedSchema?.value?.split('.')?.[1] || null;

    return schemaData ? (
        <div className="schema-reference-page">
            <div className="callout mt-2 mb-2">
                <p className="mb-2">
                    The <b>Submission Data Dictionary</b> provides a reference
                    of metadata items and properties included in the Submission
                    Spreadsheet. You can search for specific metadata items or
                    properties using the dropdown below.
                </p>
                <p>
                    Search by: <br />
                    <b>Item: </b> type in the name of the item in the search bar
                    below (e.g. <i>FamilyHistory</i>).
                    <br />
                    <b>Property: </b> type in the name of the property (e.g.{' '}
                    <i>disease</i>).
                    <br />
                    <b>Specific property in an item: </b> type
                    &lt;item&gt;.&lt;property&gt; (e.g.{' '}
                    <i>familyhistory.disease</i>).
                </p>
                <p className="mb-2">
                    Note: Everything other than age, sex, hardy scale are
                    protected under dbGaP. No protected data is contained in
                    this page.
                </p>
            </div>
            <hr className="my-4"></hr>
            <Select
                value={selectedSchema}
                placeholder="Select a tab/item type from the Submission Spreadsheet (e.g. AlignedReads or Analyte)..."
                onChange={(selectedItem) => {
                    setSelectedSchema(selectedItem);
                }}
                options={options}
            />
            <div className="callout mt-2">
                <p>
                    <b>Note:</b> Metadata Property names in{' '}
                    <span className="text-danger fw-bold">RED</span> are
                    required for all items of that item type.
                </p>
            </div>
            {selectedSchema?.value && (
                <div
                    className={`selected-schema schema-item ${selectedSchema.value} table-responsive`}>
                    <h3 className="fs-4">{selectedSchema.value}</h3>
                    {selectedSchemaItem && (
                        <>
                            <SchemaPropertiesTable
                                data={
                                    schemaData[selectedSchemaItem]?.properties
                                }
                                selectedProperty={selectedSchemaProperty}
                            />
                            <hr className="my-5"></hr>
                        </>
                    )}
                </div>
            )}
            <div className="schemas-container">
                {Object.keys(schemaData).map((schemaKey, i) => {
                    return (
                        <div
                            id={schemaKey}
                            className={`schema-item ${schemaKey} mb-5 table-responsive`}
                            key={i}>
                            <h3 className="fs-4">{schemaKey}</h3>
                            <SchemaPropertiesTable
                                schemaKey={schemaKey}
                                data={schemaData[schemaKey]?.properties}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    ) : (
        <div>
            <i className="icon icon-spin icon-spinner"></i>
        </div>
    );
};

const backup_property_values = new Map([
    [
        'Donor',
        [
            {
                title: 'external_id',
                description: 'External ID for the item provided by submitter',
            },
        ],
    ],
    [
        'ProtectedDonor',
        [
            {
                title: 'external_id',
                description: 'External ID for the item provided by submitter',
            },
        ],
    ],
    [
        'AlignedReads',
        [
            {
                title: 'data_type',
                description: 'Detailed type of information in the file',
            },
        ],
    ],
    [
        'DeathCircumstances',
        [
            {
                title: 'circumstances_of_death',
                description: 'The manner or context in which death occurred',
            },
        ],
    ],
]);
